import classNames from "classnames";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import DropDown from "PFComponents/dropdown/dropdown";
import { Icon } from "PFComponents/icon";
import { LoadingDots } from "PFComponents/loading_dots";
import { useActivityUpdate } from "PFCore/hooks/queries/activity";
import { useCustomValuesOptions } from "PFCore/hooks/queries/custom_values/use_custom_values_options";
import PropTypes from "prop-types";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { PriorityBadge } from "../priority_badge";
import css from "./priority_dropdown.module.scss";

const PriorityDropdown = ({ activity, disabled, required, onSelected }) => {
  const { t } = useTranslation("workflow", { keyPrefix: "parts.threeDotsMenu" });
  const { showGrowl } = useGrowlStoreActions();
  const { update: updateActivity, isUpdating } = useActivityUpdate(activity.id);

  const selectedPriorityId = activity.priority?.[0]?.id;

  const { data, isFetching } = useCustomValuesOptions({ type: "priority" });

  const handleSetPriority = (priority) => {
    if (isUpdating) {
      return;
    }
    updateActivity({
      customFields: [{ type: { name: "priority" }, values: priority ? [{ id: priority.id }] : [] }]
    }).then(
      () => {
        showGrowl({ message: t("priority.success") });
        onSelected && onSelected(priority);
        activity.priority = priority ? [{ id: priority.id, text: priority.text }] : null;

        // Hack to close parent dropdown
        const fakeButton = document.createElement("button");
        const parent = document.getElementById("react_app");
        parent.appendChild(fakeButton);
        fakeButton.click();
        fakeButton.remove();
      },
      () => {
        showGrowl({ message: t("priority.error"), kind: "error" });
      }
    );
  };

  const renderDisplayElement = useCallback(
    (option = {}, className) => (
      <div className={classNames(css.priorityOption, className, { [css.loading]: isUpdating })}>
        <PriorityBadge level={option.text?.toLowerCase()} />
        <span className={css.optionText}>{option.text || t("translation:none")}</span>
        {selectedPriorityId === option.id ? (
          <Icon name="check" size="sm" />
        ) : (
          <div className={css.iconPlaceholder} />
        )}
      </div>
    ),
    [isUpdating, selectedPriorityId, t]
  );

  const formattedOptions = useMemo(() => {
    if (isFetching) {
      return [
        {
          id: "loading",
          displayElement: <LoadingDots />,
          item: null
        }
      ];
    }
    const result = (data ?? []).map((option) => ({
      id: option.id,
      displayElement: renderDisplayElement(option),
      item: option,
      disabled
    }));

    result.push({
      id: "none",
      displayElement: renderDisplayElement({}, css.none),
      item: null,
      disabled: disabled || required
    });

    return result;
  }, [isFetching, data, renderDisplayElement, disabled, required]);

  return (
    <DropDown
      rootClassName={css.priorityDropdown}
      options={formattedOptions}
      handleChange={(item) => handleSetPriority(item)}
    />
  );
};

PriorityDropdown.propTypes = {
  onSelected: PropTypes.func,
  activity: PropTypes.shape({
    id: PropTypes.number.isRequired,
    priority: PropTypes.array
  }),
  disabled: PropTypes.bool,
  required: PropTypes.bool
};

export default PriorityDropdown;
